import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import "./trurecipe-pages.scss";
import "./guided-instructions.scss";

/**
 * Guided Work Instructions — AR strip: equal outline tiles (72×72), crosshair+Tool,
 * empty yellow frame, four-corner focus + Zone; viewport #0d1117 / #ffcc00 frame.
 */

const TOTAL_STEPS = 5;

const CHECKLIST = [
  { id: "wp", label: "Workpiece secured" },
  { id: "tool", label: "Tool verified" },
  { id: "guard", label: "Safety guard in place" },
];

const SAFETY_ALERTS = ["Wear eye protection", "Hot surface — allow 30min cooldown"];

function GaugeCard(props: {
  title: string;
  target: string;
  current: string;
  range: string;
  thumbPct: number;
}) {
  return (
    <div className="tv-guided__gauge">
      <div className="tv-guided__gauge-head">
        <span>{props.title}</span>
        <i className="fa fa-check" aria-hidden />
      </div>
      <div className="tv-guided__gauge-body">
        <div className="tv-guided__gauge-values">
          <div>
            <strong>Target:</strong> {props.target}
          </div>
          <div>
            <strong>Current:</strong>{" "}
            <span className="tv-guided__gauge-current">{props.current}</span>
          </div>
        </div>
        <div className="tv-guided__gauge-track">
          <span
            className="tv-guided__gauge-dot"
            style={{ left: `${props.thumbPct}%` }}
            aria-hidden
          />
        </div>
        <div className="tv-guided__gauge-range">{props.range}</div>
      </div>
    </div>
  );
}

const GuidedInstructions = () => {
  const [step, setStep] = useState(0);
  const [checks, setChecks] = useState<Record<string, boolean>>({});

  const pct = useMemo(() => Math.round(((step + 1) / TOTAL_STEPS) * 100), [step]);

  const toggleCheck = (id: string) => {
    setChecks((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="tv-mes-v2-page">
      <div className="tv-guided">
        <Link to="/mes-trurecipe/library" className="tv-guided__back">
          <i className="fa fa-arrow-left" aria-hidden />
          <span>Back to Recipe Library</span>
        </Link>

        <div className="tv-guided__grid">
          <main className="tv-guided__main">
            <div className="tv-guided__progress-row">
              <span className="tv-guided__step-label">
                Step {step + 1} of {TOTAL_STEPS}
              </span>
              <span className="tv-guided__pct tv-guided__pct--muted">{pct}% Complete</span>
            </div>
            <div
              className="tv-guided__bar"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="tv-guided__bar-fill" style={{ width: `${pct}%` }} />
            </div>

            <div className="tv-guided__ar-status">
              <i className="fa fa-eye tv-guided__ar-eye" aria-hidden />
              <input type="checkbox" className="tv-guided__ar-ck" aria-label="AR glasses connection" />
              <span>AR Glasses Connected</span>
            </div>

            <div className="tv-guided__viewport-wrap">
              <div className="tv-guided__viewport" aria-label="AR camera view placeholder">
                <div className="tv-guided__viewport-inner">
                  <div className="tv-guided__ar-icons">
                    <div className="tv-guided__ar-tile tv-guided__ar-tile--tool">
                      <i className="fa fa-crosshairs" aria-hidden />
                      <span>Tool</span>
                    </div>
                    <div className="tv-guided__ar-tile tv-guided__ar-tile--frame" aria-hidden />
                    <div className="tv-guided__ar-tile tv-guided__ar-tile--zone">
                      <span className="tv-guided__focus-brackets" aria-hidden>
                        <span className="tv-guided__focus-brackets__c tv-guided__focus-brackets__c--tl" />
                        <span className="tv-guided__focus-brackets__c tv-guided__focus-brackets__c--tr" />
                        <span className="tv-guided__focus-brackets__c tv-guided__focus-brackets__c--bl" />
                        <span className="tv-guided__focus-brackets__c tv-guided__focus-brackets__c--br" />
                      </span>
                      <span>Zone</span>
                    </div>
                  </div>
                  <p className="tv-guided__viewport-caption">AR Camera View — Safety Zone Active</p>
                </div>
              </div>
            </div>

            <h2 className="tv-guided__checklist-title">Step checklist</h2>
            <div className="tv-guided__checklist">
              {CHECKLIST.map((c) => (
                <label key={c.id} className="tv-guided__check">
                  <input
                    type="checkbox"
                    checked={Boolean(checks[c.id])}
                    onChange={() => toggleCheck(c.id)}
                  />
                  {c.label}
                </label>
              ))}
            </div>

            <div className="tv-guided__actions">
              <button type="button" className="tv-guided__action tv-guided__action--photo">
                <i className="fa fa-camera" aria-hidden />
                Capture Photo
              </button>
              <button type="button" className="tv-guided__action tv-guided__action--video">
                <i className="fa fa-video-camera" aria-hidden />
                Record Video
              </button>
              <button type="button" className="tv-guided__action tv-guided__action--done">
                <i className="fa fa-check" aria-hidden />
                Mark Setup Complete
              </button>
            </div>

            <div className="tv-guided__nav">
              <button
                type="button"
                className="tv-guided__nav-btn"
                disabled={step <= 0}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
              >
                <i className="fa fa-chevron-up" aria-hidden />
                Previous
              </button>
              <button
                type="button"
                className="tv-guided__nav-btn tv-guided__nav-btn--next"
                disabled={step >= TOTAL_STEPS - 1}
                onClick={() => setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1))}
              >
                Next
                <i className="fa fa-chevron-down" aria-hidden />
              </button>
            </div>
          </main>

          <aside className="tv-guided__side" aria-label="Live IoT parameters">
            <div className="tv-guided__iot">
              <div className="tv-guided__iot-head">
                <i className="fa fa-wifi" aria-hidden />
                IoT Health
              </div>
              <p className="tv-guided__iot-meta">2 of 3 parameters OK</p>
              <p className="tv-guided__iot-meta tv-guided__iot-meta--muted" style={{ marginTop: 4 }}>
                Last sync: 2 seconds ago
              </p>
            </div>

            <div className="tv-guided__alert" role="status">
              <i className="fa fa-exclamation-triangle" aria-hidden />
              <span>
                Parameter Alert: Coolant Pressure drifting — 3.1 bar (target: 3.2)
              </span>
            </div>

            <GaugeCard
              title="Spindle Speed"
              target="1800 RPM"
              current="1805 RPM"
              range="1700 - 1900 RPM"
              thumbPct={52}
            />
            <GaugeCard
              title="Feed Rate"
              target="0.15 mm/rev"
              current="0.152 mm/rev"
              range="0.12 - 0.18 mm/rev"
              thumbPct={58}
            />
            <GaugeCard
              title="Coolant Pressure"
              target="3.2 bar"
              current="3.1 bar"
              range="3 - 3.5 bar"
              thumbPct={28}
            />

            <div className="tv-guided__qc">
              <strong>Quality Checkpoint</strong>
              <span>Auto-validated</span>
            </div>
          </aside>
        </div>

        <section className="tv-guided__safety" aria-label="Safety zone alerts">
          <h2 className="tv-guided__safety-title">
            <i className="fa fa-shield" aria-hidden />
            Safety Zone Alerts
          </h2>
          <div className="tv-guided__safety-list">
            {SAFETY_ALERTS.map((msg) => (
              <div key={msg} className="tv-guided__safety-banner">
                <i className="fa fa-exclamation-triangle" aria-hidden />
                {msg}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default GuidedInstructions;
